import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "Is SAINT FDA-cleared, and for what specifically?",
    answer:
      "SAINT received FDA Breakthrough Device Designation and was cleared in 2022 specifically for adults with major depressive disorder who haven't gotten adequate relief from antidepressant medication in their current episode. That's the population it was studied in and the indication it carries. Many treatments marketed to people with treatment-resistant depression — retreats, IV protocols, supplements, off-label combinations — have no regulatory clearance at all, or clearance for something other than depression.",
  },
  {
    question: "What's the difference between 'response' and 'remission'?",
    answer:
      "Response is a 50 percent reduction in symptoms — a person at 50 percent is still depressed. Remission means symptoms are reduced to the point where you no longer meet criteria for the illness. In SAINT's clinical trials, 79 percent of participants reached remission, not merely response, with an average time to remission of 2.6 days. When evaluating any treatment, ask for its remission rate in treatment-resistant patients specifically. If they give you a response rate instead, or a number from a general population, note the substitution.",
  },
  {
    question: "How is SAINT's mechanism different from antidepressants?",
    answer:
      "Every antidepressant works on brain chemistry — adjusting the chemical environment between neurons. If you've failed three antidepressants, a fourth is the same mechanism with a different label. Depression is also a circuitry problem: the region behind the forehead that regulates mood, motivation, and attention is underactive, and its connections to the rest of the mood network are weakened. SAINT works on that circuit directly, using focused magnetic stimulation guided by an image of your own brain. It's a different mechanism, aimed at the layer your medications never reached.",
  },
  {
    question: "What are the side effects and risks of SAINT?",
    answer:
      "Across more than 400 patients in clinical trials, no serious adverse events were reported. The most common side effects were mild headache, fatigue, and temporary scalp discomfort at the treatment site. Nothing enters your bloodstream, so there's no interaction with existing medications, which don't need to be stopped. SAINT isn't appropriate for people with non-removable metal in or near the head or an implanted medical device, and you'll be screened for these at consultation.",
  },
  {
    question: "How long does SAINT take compared to other treatments?",
    answer:
      "A medication trial is six to twelve weeks to find out if it worked. Standard TMS is 30 to 36 daily visits over six to nine weeks. ECT is several weeks of sessions under anesthesia. SAINT is five consecutive days: ten 10-minute sessions per day with 50-minute breaks between them, following a 45-minute fMRI and algorithm-driven targeting. Five clinic visits. One week.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      "You've Tried Everything Money Can Buy. Here's What You Haven't Tried.",
    href: "/youve-tried-everything-money-can-buy",
    description:
      "Why the best-resourced psychiatric care often ends up the most outdated, and where the real frontier is now.",
  },
  {
    title: "The Executive Who Can't Afford Six Weeks",
    href: "/the-executive-who-cant-afford-six-weeks",
    description:
      "Why high performers get stuck in partial treatment, and why the schedule is the real barrier.",
  },
  {
    title: "How SAINT® Compares to Other Depression Treatment Options",
    href: "/how-saint-compares-to-other-depression-treatment-options",
    description:
      "A side-by-side comparison of SAINT® against medication, standard TMS, esketamine, and ECT.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function DueDiligenceDepressionTreatment() {
  return (
    <DepressionContentPage
      seo={{
        title: "How to Do Due Diligence on a Depression Treatment",
        description:
          "An eight-question framework for evaluating any depression treatment — FDA status, remission rates, mechanism, time cost, and provider qualifications — using SAINT® as the worked example.",
        canonical: "/how-to-do-due-diligence-on-a-depression-treatment",
        keywords:
          "depression treatment due diligence, SAINT TMS FDA clearance, remission rate depression treatment, treatment-resistant depression evaluation, brain stimulation depression, fMRI-guided TMS, Magnus Medical verified provider",
      }}
      badge="Patient Guide"
      heroTitle="How to Do Due Diligence on a Depression Treatment"
      heroSubtitle="You wouldn't put capital into a company because a friend said it was good. You'd want the numbers, the track record, the regulatory status, and the names behind it. For some reason, most people abandon that discipline when it comes to their own depression. Here's the framework, using SAINT® as the worked example."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                You wouldn't put capital into a company because a friend said it
                was good. You'd want the numbers, the track record, the
                regulatory status, and the names behind it. You'd want to know
                what "success" means in their terms versus yours. And you'd be
                suspicious of anything that sounded too good, or that couldn't
                produce data on request.
              </p>
              <p>
                For some reason, most people abandon that discipline when it
                comes to their own depression. They try what a doctor suggests,
                then what a friend suggests, then what an ad suggests, without
                ever asking the questions they'd ask about any other serious
                decision.
              </p>
              <p>
                If you've been through several treatments that didn't work, and
                you're evaluating what comes next, this article gives you the
                framework. It applies to any option you're considering. We'll
                walk through it using SAINT as the worked example, because it's
                the treatment we offer and because it holds up.
              </p>
            </div>
          ),
        },
        {
          heading: "Question 1: Is it FDA-cleared, and for what?",
          body: (
            <div className="space-y-5">
              <p>
                This is the first filter, and it eliminates a surprising amount
                of what gets marketed to people with treatment-resistant
                depression. Retreats, IV protocols, supplements, off-label
                combinations, and "proprietary" methods often have no regulatory
                clearance at all, or clearance for something other than
                depression.
              </p>
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                received FDA Breakthrough Device Designation and was cleared in
                2022 specifically for adults with major depressive disorder who
                haven't gotten adequate relief from antidepressant medication in
                their current episode. That's the population you're in. That's
                the indication.
              </p>
            </div>
          ),
        },
        {
          heading: "Question 2: Where did it come from?",
          body: (
            <div className="space-y-5">
              <p>
                Track record matters. Was the treatment developed by a company
                with a product to sell, or by an academic institution with a
                research question?
              </p>
              <p>
                SAINT was developed at Stanford's Brain Stimulation Lab by
                psychiatrists and neuroscientists studying why depression fails
                to respond to medication. It was published in peer-reviewed
                journals, replicated in a randomized controlled trial, and only
                then commercialized. The science came first.
              </p>
            </div>
          ),
        },
        {
          heading:
            'Question 3: What does "works" mean, and what\'s the number?',
          body: (
            <div className="space-y-5">
              <p>
                This is where most treatments hide. "Improvement" and "response"
                are low bars; a 50 percent reduction in symptoms counts as
                response, and a person at 50 percent is still depressed. The
                number that matters is remission: symptoms reduced to the point
                where you no longer meet criteria for the illness.
              </p>
              <p>
                In SAINT's clinical trials, 79 percent of participants reached
                remission. Not response. Remission. The average time to get
                there was 2.6 days. Across more than 400 trial patients, 85
                percent experienced symptom relief.
              </p>
              <p>
                For context, remission rates in similar treatment-resistant
                populations: another antidepressant trial, about 14 percent;
                esketamine, 16 to 37 percent; standard TMS, 17 to 32 percent;
                ECT, 48 percent. Ask any treatment you're evaluating for its
                remission rate in treatment-resistant patients, specifically. If
                they give you a response rate instead, or a number from a
                general population, note the substitution.
              </p>

              <SaintComparisonStatBand />
            </div>
          ),
        },
        {
          heading:
            "Question 4: What's the mechanism, and is it different from what already failed?",
          body: (
            <div className="space-y-5">
              <p>
                If you've failed three antidepressants, a fourth antidepressant
                is not diversification. It's the same bet with a different
                label. Every medication works on brain chemistry; that's the
                mechanism that's already come up short for you.
              </p>
              <p>
                Depression is also a circuitry problem. The region behind the
                forehead that regulates mood, motivation, and attention is
                underactive, and its connections to the rest of the mood network
                are weakened. SAINT works on that circuit directly, using
                focused magnetic stimulation guided by an image of your own
                brain. It's a different mechanism, aimed at the layer your
                medications never reached.
              </p>
            </div>
          ),
        },
        {
          heading: "Question 5: What's the downside?",
          body: (
            <div className="space-y-5">
              <p>
                Every serious evaluation asks what happens if it goes wrong. For
                medication, the downside is months of side effects for a low
                probability of remission. For ECT, it's anesthesia and a known
                risk of memory problems. For esketamine, it's dissociation,
                monitoring, and open-ended maintenance dosing.
              </p>
              <p>
                For SAINT, the trials reported no serious adverse events across
                more than 400 patients. The most common side effects were mild
                headache, fatigue, and temporary scalp discomfort. Nothing
                enters your bloodstream, so there's no interaction with existing
                medications, which don't need to be stopped. It isn't
                appropriate for people with non-removable metal in or near the
                head or an implanted device.
              </p>
            </div>
          ),
        },
        {
          heading: "Question 6: What's the time cost?",
          body: (
            <div className="space-y-5">
              <p>
                Time is the resource that doesn't come back. A medication trial
                is six to twelve weeks to find out if it worked. Standard TMS is
                30 to 36 daily visits over six to nine weeks. ECT is several
                weeks of sessions under anesthesia.
              </p>
              <p>
                SAINT is five consecutive days: ten 10-minute sessions per day
                with 50-minute breaks between them, following a 45-minute fMRI
                and algorithm-driven targeting. Five clinic visits. One week.
              </p>
            </div>
          ),
        },
        {
          heading: "Question 7: Who's delivering it, and are they qualified?",
          body: (
            <div className="space-y-4">
              <p>
                A protocol is only as good as its execution. SAINT depends on
                precise imaging, correct targeting, and neuronavigation that
                keeps the coil on target for every session. That requires
                specific equipment and training, and Magnus Medical, the company
                that commercialized SAINT, designates providers who meet its
                standards as Verified SAINT Providers.
              </p>
              <p>
                Neuro Wellness TMS Centers of America is a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>
                . Here's what the process looks like with us:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation.</strong> Our clinical team reviews your
                  full treatment history and confirms candidacy. We expect the
                  history to be long. Bring it. The initial consultation is a
                  Zoom call; as steps evolve, you'll also have the option to
                  meet with our psychiatrist by Zoom web conference or in
                  person. The mandatory in-person steps include fMRI mapping and
                  the actual treatment.
                </li>
                <li>
                  <strong>fMRI brain scan.</strong> A roughly 45-minute
                  functional MRI maps how your individual brain networks are
                  wired.
                </li>
                <li>
                  <strong>Personalized targeting.</strong> The targeting
                  algorithm reads your scan and identifies the exact stimulation
                  point. Neuronavigation guides the coil there, session after
                  session.
                </li>
                <li>
                  <strong>Five-day stimulation series.</strong> Ten sessions a
                  day for five days, with a dedicated care team, in a private
                  outpatient setting, with weekend availability if your schedule
                  requires it.
                </li>
              </ol>
            </div>
          ),
        },
        {
          heading:
            "Question 8: What does it cost, and does that change the decision?",
          body: (
            <div className="space-y-5">
              <p>
                Insurance coverage for SAINT is expanding but still varies by
                plan; some cover it with prior authorization, others don't yet.
                Self-pay is available. Our team will tell you exactly where you
                stand before anything is scheduled.
              </p>
              <p>
                If you've already spent years and significant money on
                treatments that didn't work, the relevant question isn't whether
                SAINT costs more than a prescription. It's whether it's the
                best-evidenced option for the problem you actually have. On the
                questions above, it is.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                The consultation is the due-diligence meeting. Bring your
                questions, bring your history, and expect straight answers,
                including "no" if SAINT isn't the right fit.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you are in crisis or thinking about harming yourself, call or
                text 988 (Suicide &amp; Crisis Lifeline) or go to your nearest
                emergency room. This article is for educational purposes and is
                not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
